/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import { Info, Search } from "lucide-react";
import type { ApiResult } from "../api/client";
import type { Paged } from "../api/registry";
import { ErrorMessage } from "./ui/ErrorMessage";
import { InfiniteList } from "./ui/InfiniteList";
import { Input } from "./ui/Input";
import { Spinner } from "./ui/Spinner";
import { Tooltip } from "./ui/Tooltip";
import { EmptyState } from "./ui/EmptyState";
import { debounce } from "../lib/utils";

const DEBOUNCE_MILLIS = 300;

/**
 * The one registry directory shell: debounced search over a paged nexus list, rendered as cards.
 * Organizations and providers differ only in their card, so the card is a prop.
 */
export type EmptyCopy = { icon: ReactNode; title: string; description: string };

export function RegistryDirectory<T>({
  placeholder,
  countNoun,
  countHint,
  load,
  itemKey,
  renderCard,
  empty,
  controls,
  filters,
  filterKey = "",
}: {
  placeholder: string;
  countNoun: string;
  countHint?: string;
  /** Fixed-width controls that stay beside the search box, such as a sort select. */
  controls?: ReactNode;
  /** The filter bar; its active-filter chips take the row below the search. */
  filters?: ReactNode;
  filterKey?: string;
  load: (q: string, page: number) => Promise<ApiResult<Paged<T>>>;
  itemKey: (item: T) => number | string;
  renderCard: (item: T, index: number) => ReactNode;
  empty: EmptyCopy;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const [draft, setDraft] = useState(q);
  const setQ = (value: string) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === "") {
          next.delete("q");
        } else {
          next.set("q", value);
        }
        return next;
      },
      { replace: true },
    );
  };
  const [items, setItems] = useState<T[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useMemo(
    () => debounce((value: string) => setQ(value), DEBOUNCE_MILLIS),
    [],
  );
  useEffect(() => () => debouncedQuery.cancel(), [debouncedQuery]);

  const requestSeq = useRef(0);
  const loadPage = (pageToLoad: number) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    void load(q, pageToLoad).then((result) => {
      if (seq !== requestSeq.current) {
        return;
      }
      setLoading(false);
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      setError(null);
      setHasMore(result.value.hasMore);
      setTotal(result.value.total);
      setPage(pageToLoad);
      if (pageToLoad === 0) {
        setItems(result.value.items);
        return;
      }
      setItems((previous) => {
        const seen = new Set((previous ?? []).map(itemKey));
        return [
          ...(previous ?? []),
          ...result.value.items.filter((item) => !seen.has(itemKey(item))),
        ];
      });
    });
  };
  const loadPageRef = useRef(loadPage);
  loadPageRef.current = loadPage;

  useEffect(() => {
    setItems(null);
    loadPageRef.current(0);
  }, [q, filterKey]);

  return (
    <div className="space-y-6">
      <div
        className="enter flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center"
        style={{ "--enter-delay": "80ms" } as CSSProperties}
      >
        <div className="min-w-0 w-full flex-1 sm:max-w-xl">
          <Input
            aria-label={placeholder}
            placeholder={placeholder}
            prefix={<Search size={15} className="text-text-tertiary" />}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              debouncedQuery(event.target.value);
            }}
          />
        </div>
        {controls}
        {filters}
      </div>

      {items === null && error !== null ? (
        <ErrorMessage message={error} />
      ) : items === null ? (
        <div className="grid place-items-center py-20">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-6">
          <p
            className="flex items-center gap-1.5 text-sm text-text-tertiary"
            aria-live="polite"
          >
            <span>
              {total === 1
                ? `1 ${countNoun}`
                : `${total.toLocaleString("en-US")} ${countNoun}s`}
            </span>
            {countHint && (
              <Tooltip
                content={countHint}
                className="max-w-xs whitespace-normal text-left"
              >
                <Info
                  size={14}
                  aria-label="About this count"
                  className="text-text-tertiary"
                />
              </Tooltip>
            )}
            {loading && (
              <Spinner size="xs" className="inline-block align-middle" />
            )}
          </p>
          <InfiniteList
            isLoading={loading}
            hasMore={hasMore}
            onLoadMore={() => loadPageRef.current(page + 1)}
            isEmpty={items.length === 0}
            currentPage={page}
            error={error}
            onRetry={() => loadPageRef.current(page + 1)}
            emptyState={
              <EmptyState
                icon={empty.icon}
                tone={q === "" ? "quiet" : "search"}
                title={empty.title}
                description={empty.description}
                actionLabel={q === "" ? undefined : "Clear search"}
                onAction={
                  q === ""
                    ? undefined
                    : () => {
                        setDraft("");
                        setQ("");
                      }
                }
              />
            }
          >
            <ol className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {items.map((item, index) => renderCard(item, index))}
            </ol>
          </InfiniteList>
        </div>
      )}
    </div>
  );
}
